"use client";

import { MarketContexts } from "@decibeltrade/sdk";
import { useEffect } from "react";

import { env } from "@/env";

import { readDexSdk } from "../dex-sdk";
import { marketContextsQueryKey } from "../queries/market-context";
import { useRegisterSubscription } from "../subscription-provider";

/**
 * Writes frames into the same query key `marketContextsQuery` fills over HTTP, so a
 * component reading that key goes live without knowing the socket exists; HTTP stays
 * the cold-start seed and the repair after a reconnect.
 *
 * Off by default: a server without the topic rejects the subscribe, and a rejected
 * subscribe is a `console.error` in every session that opens one.
 */
export function useMarketContextsSubscription() {
  const registerSubscription = useRegisterSubscription();

  useEffect(() => {
    if (!env.NEXT_PUBLIC_ENABLE_MARKET_CONTEXTS_WS) return;
    const queryKey = marketContextsQueryKey();

    return registerSubscription(queryKey, (queryClient) => {
      const unsubscribe = readDexSdk.marketContexts.subscribeAll((data) => {
        // Each message is a full snapshot, same shape as the HTTP response.
        queryClient.setQueryData<MarketContexts>(queryKey, data.contexts);
      });

      const offReconnect = readDexSdk.onWsReconnect(() =>
        queryClient.invalidateQueries({ queryKey }),
      );

      return () => {
        unsubscribe();
        offReconnect();
      };
    });
  }, [registerSubscription]);
}
